import { CalendarDays } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { Avatar } from '@/components/ui/avatar'
import { Select } from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { fmtDate, fmtMoney } from '../../shared/format'
import { useOffers } from '../api'
import { STAGES, type Customer, type Stage } from '../types'
import { useCrm } from '../ui'
import { CustomerAvatar } from './CrmBits'

const BOARD: Exclude<Stage, 'lost'>[] = ['lead', 'demo', 'proposal', 'customer']
const COLUMN_TONE = { lead: 'bg-violet/12 text-violet', demo: 'bg-info/12 text-info', proposal: 'bg-brand-orange/12 text-brand-orange', customer: 'bg-success/12 text-success' }

type Props = { rows: Customer[]; canWrite: boolean; onStage: (c: Customer, s: Stage) => void }

/** Müşteriler listesinin pano görünümü: satış aşamalarına göre sütunlar. */
export function CustomerBoard({ rows, canWrite, onStage }: Props) {
  const { t } = useTranslation()
  const offers = useOffers()
  const { staffName } = useCrm()
  const lastOffer = (id: string) => (offers.data ?? []).find((o) => o.customerId === id)

  return (
    <div className="scrollbar-none -mx-3 flex snap-x gap-3 overflow-x-auto px-3 pb-1 sm:mx-0 sm:grid sm:grid-cols-2 sm:overflow-visible sm:px-0 xl:grid-cols-4">
      {BOARD.map((stage) => {
        const cards = rows.filter((c) => c.stage === stage)
        return (
          <section key={stage} className="flex w-72 shrink-0 snap-start flex-col rounded-xl border bg-muted/30 p-2 sm:w-auto" aria-label={t(`crm.stage.${stage}`)}>
            <header className="mb-2 flex items-center justify-between gap-2 rounded-lg bg-card px-3 py-2 shadow-xs">
              <h2 className="flex items-center gap-2 font-semibold">
                <span className={cn('size-2.5 rounded-full bg-current', COLUMN_TONE[stage])} aria-hidden />
                {t(`crm.stage.${stage}`)}
              </h2>
              <span className={cn('rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums', COLUMN_TONE[stage])}>{cards.length}</span>
            </header>
            <ul className="space-y-2">
              {cards.map((c) => {
                const offer = lastOffer(c.id)
                return (
                  <li key={c.id} className="space-y-2 rounded-lg border bg-card p-3 shadow-card">
                    <Link to={`/customers/${c.id}`} className="flex items-center gap-2 hover:text-primary">
                      <CustomerAvatar name={c.name} className="size-8 text-xs" />
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-semibold">{c.name}</span>
                        <span className="block truncate text-xs text-muted-foreground">{c.district}</span>
                      </span>
                    </Link>
                    {offer ? (
                      <p className="flex items-center justify-between gap-2 text-xs">
                        <span className="truncate font-mono text-muted-foreground">{offer.number}</span>
                        <span className="font-semibold tabular-nums">{fmtMoney(offer.total)}</span>
                      </p>
                    ) : null}
                    {c.nextAction ? (
                      <p className={cn('flex items-center gap-1 text-xs', c.nextAction.overdue ? 'text-destructive' : 'text-muted-foreground')}>
                        <CalendarDays className="size-3" aria-hidden />
                        <span className="truncate">
                          {fmtDate(c.nextAction.date)} · {c.nextAction.text}
                        </span>
                      </p>
                    ) : null}
                    <div className="flex items-center justify-between gap-2 border-t pt-2">
                      <span className="flex min-w-0 items-center gap-1.5 text-xs">
                        <Avatar name={staffName(c.ownerId)} className="size-5 [&>span]:text-[8px]" />
                        <span className="truncate">{staffName(c.ownerId)}</span>
                      </span>
                      {canWrite ? (
                        <Select
                          size="xs"
                          className="w-28"
                          value={c.stage}
                          onChange={(s) => onStage(c, s)}
                          aria-label={t('crm.changeStage')}
                          options={STAGES.map((s) => ({ value: s, label: t(`crm.stage.${s}`) }))}
                        />
                      ) : null}
                    </div>
                  </li>
                )
              })}
              {cards.length === 0 ? <li className="px-2 py-6 text-center text-xs text-muted-foreground">{t('crm.board.emptyColumn')}</li> : null}
            </ul>
          </section>
        )
      })}
    </div>
  )
}
